import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import ReferralProgram from "../components/ReferralProgram";
import { HeroSection } from "@/components/home/HeroSection";
import { ArtistsWall } from "@/components/home/ArtistsWall";
import { PayLaterStrip } from "@/components/home/PayLaterStrip";
import { FavouritesSection } from "@/components/home/FavouritesSection";
import { RoomsShowcase } from "@/components/home/RoomsShowcase";
import { FilmedSetsSection } from "@/components/home/FilmedSetsSection";
import { ReviewsSection } from "@/components/home/ReviewsSection";
import { CoursesSection } from "@/components/home/CoursesSection";
import { PayLaterPill } from "@/components/home/SectionHeading";
import { ChristmasVoucherBanner } from "@/components/vouchers/ChristmasVoucherBanner";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-dream-bg">
      <HeroSection />
      <ChristmasVoucherBanner scope="all" />
      <ArtistsWall />
      <FavouritesSection />
      <PayLaterStrip />
      <RoomsShowcase />
      <FilmedSetsSection />
      <ReviewsSection />
      <CoursesSection />

      {/* Experience Section */}
      <section className="py-24 md:py-32 bg-dream-bg" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <p className="text-dream-violet font-mono text-xs tracking-[0.3em] mb-4 uppercase">The Dream Makers Difference</p>
              <h2 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight leading-[1.02]">
                15+ years of<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-dream-violet to-cyan-300">industry experience.</span>
              </h2>
              <p className="text-white/60 mb-6">
                Our in-house engineer brings over 15 years of professional industry experience, 
                supported by a highly skilled team in audio and music production. We're not just 
                a studio—we're your creative partners.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "State-of-the-art recording equipment",
                  "Acoustically treated spaces",
                  "Comfortable creative environment",
                  "Expert guidance and support"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <div className="w-2 h-2 bg-dream-violet rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button className="bg-dream-violet text-white hover:bg-dream-violet-glow rounded-full px-8 py-6">
                  Learn More About Us
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/l1f1gvkm_DSC_0131.jpg"
                  alt="Dream Makers Lounge"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-dream-bg p-6 rounded-xl border border-dream-border shadow-2xl">
                <p className="font-heading text-5xl font-extrabold text-white">15+</p>
                <p className="text-white/60 text-sm">Years Experience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
      <ReferralProgram />

      {/* Follow Our Socials Section */}
      <section className="py-24 md:py-32 bg-dream-bg relative overflow-hidden" data-testid="instagram-section">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Stay Connected</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Follow Our Socials
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto mb-12 text-lg">
              Go behind the scenes, see our latest sessions, and stay connected with the Dream Makers community. 
              Join thousands of artists and music lovers!
            </p>
            
            {/* Instagram Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="max-w-md mx-auto"
            >
              <a 
                href="https://www.instagram.com/dreammakerstudioau" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-1 rounded-2xl">
                  <div className="bg-dream-surface rounded-2xl p-8 group-hover:bg-dream-surface/80 transition-colors">
                    {/* Instagram Icon */}
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-2xl flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    
                    {/* Handle */}
                    <h3 className="font-heading text-2xl font-bold text-white mb-2">
                      @dreammakerstudioau
                    </h3>
                    <p className="text-white/60 mb-6">
                      Studio sessions • Behind the scenes • Artist features
                    </p>
                    
                    {/* Follow Button */}
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-semibold px-8 py-3 rounded-full group-hover:shadow-lg group-hover:shadow-pink-500/25 transition-all">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Follow on Instagram
                    </div>
                  </div>
                </div>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-dream-bg relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-dream-violet/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight leading-[1.02]">
              Ready to create<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-dream-violet via-fuchsia-400 to-cyan-300">something amazing?</span>
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
              Book your session today and let's bring your creative vision to life. 
              All genres welcome.
            </p>
            <div className="mb-8 flex justify-center"><PayLaterPill testId="cta-pay-later" /></div>
            <Link to="/booking">
              <Button 
                data-testid="cta-book-btn"
                className="bg-white text-black hover:bg-dream-violet hover:text-white rounded-full px-10 py-6 text-lg font-bold transition-all duration-300"
              >
                Book Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
